import React, { useState, useEffect, useMemo } from 'react';
const _DEBUG = import.meta.env.VITE_DEBUG === 'true';
const VERBOSE = import.meta.env.VITE_DEBUG_VERBOSE === 'true';
import { usePropertyFilters } from '../../contexts/PropertyFilterContext';
import { useGeometryHighlight } from '../../contexts/GeometryHighlightContext';
import {
  MapPin,
  ChevronDown,
  ChevronUp,
  FileText,
  Calendar,
  Globe2,
  BadgeCheck,
  UserCheck,
} from 'lucide-react';
import { humanizeKey } from '../../utils/formatters/stringUtils';
import { formatValue } from '../../utils/formatters/valueUtils';
import { formatDateBr } from '../../utils/formatters/dateUtils';

import DataItem from './ui/DataItem';
import RecordNavigator from './ui/RecordNavigator';

// Função para formatar datas (wrapper para formatDateBr)
function formatDate(dateString) {
  if (!dateString) return '--';
  return formatDateBr(dateString);
}

/**
 * Componente para visualização de dados do Sistema de Gestão Fundiária (SIGEF)
 * de uma propriedade rural, consumindo diretamente os dados JSON do backend.
 */
const SigefTab = ({ propertyData, onHighlightGeometry }) => {
  // Removido estado para debugging

  // Acessar os códigos SIGEF selecionados do contexto
  const { selectedSigefCodes, setSelectedSigefCodes } = usePropertyFilters();

  // Log padronizado para rastreamento do fluxo de dados
  if (VERBOSE)
    // eslint-disable-next-line no-console
    console.log('[SigefTab] Recebeu dados:', {
      temDados: propertyData ? 'Sim' : 'Não',
      temProperties: propertyData?.properties ? 'Sim' : 'Não',
      temSigef: propertyData?.properties?.sigef ? 'Sim' : 'Não',
      sigefEArray: propertyData?.properties?.sigef
        ? `${Array.isArray(propertyData.properties.sigef)}`
        : 'N/A',
      tamanhoSigef:
        propertyData?.properties?.sigef && Array.isArray(propertyData.properties.sigef)
          ? propertyData.properties.sigef.length
          : 'N/A',
    });

  // Verificar se temos dados SIGEF disponíveis
  if (
    !propertyData?.properties?.sigef ||
    (Array.isArray(propertyData.properties.sigef) && propertyData.properties.sigef.length === 0)
  ) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <p className="text-gray-500">Nenhum dado SIGEF disponível para esta propriedade.</p>
      </div>
    );
  }

  // Definir estrutura de dados (memoizada para evitar novas referências a cada render)
  const sigefRaw = propertyData?.properties?.sigef;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const memoData = useMemo(() => {
    let sigefDataLocal = [];
    let parcelsLocal = [];
    let uniqueSigefCodesLocal = [];
    try {
      if (Array.isArray(sigefRaw)) {
        // eslint-disable-next-line no-console
        if (VERBOSE) console.log('[SigefTab] SIGEF é um array com', sigefRaw.length, 'itens');
        sigefDataLocal = sigefRaw;
        uniqueSigefCodesLocal = [
          ...new Set(sigefDataLocal.map((item) => item?.codigo_imo).filter(Boolean)),
        ];
        parcelsLocal = [];
        sigefRaw.forEach((sigefItem) => {
          if (sigefItem?.parcels && Array.isArray(sigefItem.parcels)) {
            parcelsLocal.push(...sigefItem.parcels);
          } else {
            parcelsLocal.push(sigefItem);
          }
        });
      } else if (sigefRaw?.details && Array.isArray(sigefRaw.details)) {
        sigefDataLocal = sigefRaw;
        parcelsLocal = sigefRaw.details;
        uniqueSigefCodesLocal = [
          ...new Set(parcelsLocal.map((item) => item?.codigo_imo).filter(Boolean)),
        ];
      } else if (sigefRaw?.parcels && Array.isArray(sigefRaw.parcels)) {
        sigefDataLocal = sigefRaw;
        parcelsLocal = sigefRaw.parcels;
        uniqueSigefCodesLocal = [
          ...new Set(parcelsLocal.map((item) => item?.codigo_imo).filter(Boolean)),
        ];
      } else if (sigefRaw) {
        // eslint-disable-next-line no-console
        if (VERBOSE) console.log('[SigefTab] SIGEF é um único objeto');
        sigefDataLocal = sigefRaw;
        parcelsLocal = [sigefRaw];
        if (sigefRaw.codigo_imo) uniqueSigefCodesLocal = [sigefRaw.codigo_imo];
      }
    } catch (_) {
      /* no-op */
    }
    return {
      sigefData: sigefDataLocal,
      parcels: parcelsLocal,
      uniqueSigefCodes: uniqueSigefCodesLocal,
    };
  }, [sigefRaw]);

  const { sigefData, parcels, uniqueSigefCodes } = memoData;

  if (VERBOSE)
    // eslint-disable-next-line no-console
    console.log('[SigefTab] Dados extraídos:', {
      sigefData: sigefData,
      totalParcelas: parcels.length,
      primeiraParcelaNome: parcels[0]?.nome_da_propriedade || parcels[0]?.parcela_co,
      codigosSelecionados: selectedSigefCodes,
    });

  // Para depuração
  // eslint-disable-next-line no-console
  if (VERBOSE) console.log('[SigefTab] Todas as parcelas extraídas:', parcels.slice(0, 3));

  // Novo algoritmo de filtragem considerando a estrutura aninhada
  let filteredParcels = [...parcels]; // Cópia para evitar mutação

  if (selectedSigefCodes.length > 0) {
    // Encontrar o item SIGEF correspondente ao código selecionado
    const filteredSigefItems = sigefData.filter((sigefItem) => {
      return selectedSigefCodes.includes(sigefItem.codigo_imo);
    });

    //console.log(`[SigefTab] Itens SIGEF filtrados por código(s): ${selectedSigefCodes.join(', ')}`, filteredSigefItems);

    // Se encontrou itens SIGEF correspondentes, usar as parcelas deles
    if (filteredSigefItems.length > 0) {
      // Extrair apenas as parcelas dos itens SIGEF selecionados
      filteredParcels = [];
      filteredSigefItems.forEach((sigefItem) => {
        if (sigefItem.parcels && Array.isArray(sigefItem.parcels)) {
          filteredParcels.push(...sigefItem.parcels);
        }
      });

      //console.log(`[SigefTab] Parcelas filtradas: ${filteredParcels.length} de ${parcels.length}`);
    } else {
      //console.log('[SigefTab] ATENÇÃO: Nenhum item SIGEF encontrado com o(s) código(s) selecionado(s), mostrando todas as parcelas');
    }
  }

  const hasParcels = parcels && parcels.length > 0;

  // Estado local para o registro selecionado e controle de visualização
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [_selectedSigefDetail, setSelectedSigefDetail] = useState(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [_selectedParcel, setSelectedParcel] = useState(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [_expandAll, _setExpandAll] = useState(false);

  // Estado para controle de navegação entre códigos SIGEF (codigo_imo)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [currentIndex, setCurrentIndex] = useState(0);

  // Definir o total de códigos SIGEF únicos
  const totalRecords = uniqueSigefCodes.length;

  // Acesso ao contexto de destaque de geometria (FlashGeometry)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { _highlightGeometryWithFlash } = useGeometryHighlight();

  // Função para navegar entre códigos SIGEF (codigo_imo)
  const handleNavigate = (newIndex) => {
    if (newIndex >= 0 && newIndex < uniqueSigefCodes.length) {
      setCurrentIndex(newIndex);

      // Seleciona o código SIGEF atual
      const currentCode = uniqueSigefCodes[newIndex];

      // Atualizar o código SIGEF selecionado no contexto de filtros
      setSelectedSigefCodes([currentCode]);
    }
  };

  // Efeito para inicializar com o primeiro código SIGEF se estiver disponível
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (uniqueSigefCodes.length > 0 && currentIndex >= uniqueSigefCodes.length) {
      setCurrentIndex(0);
    }
  }, [uniqueSigefCodes, currentIndex]);

  // Usando as funções utilitárias importadas do módulo compartilhado

  // Campos principais do SIGEF
  const _mainFields = [
    {
      key: 'parcela_co',
      accentKey: 'parcela_co',
      label: 'Código da Parcela',
      icon: <MapPin size={16} className="text-orange-500" />,
    },
    {
      key: 'nome_da_propriedade',
      accentKey: 'nome_da_propriedade',
      label: 'Nome da Propriedade',
      icon: <FileText size={16} className="text-orange-500" />,
    },
    {
      key: 'situacao_do_imovel',
      accentKey: 'situação_do_imóvel',
      label: 'Situação do Imóvel',
      icon: <BadgeCheck size={16} className="text-orange-500" />,
    },
    {
      key: 'responsavel_tecnico',
      accentKey: 'responsável_técnico',
      label: 'Responsável Técnico',
      icon: <UserCheck size={16} className="text-orange-500" />,
    },
    {
      key: 'data_da_aprovacao',
      accentKey: 'data_da_aprovação',
      label: 'Data da Aprovação',
      icon: <Calendar size={16} className="text-orange-500" />,
    },
    {
      key: 'matricula',
      accentKey: 'matrícula',
      label: 'Matrícula',
      icon: <FileText size={16} className="text-orange-500" />,
    },
  ];

  // Função para obter campo com suporte a nomes diferentes
  const _getField = (obj, key, accentKey) => {
    if (!obj) return null;
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    if (obj[accentKey] !== undefined && obj[accentKey] !== null) return obj[accentKey];
    return null;
  };

  // Efeito para selecionar parcelas com base no código SIGEF atual (deps estáveis)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!hasParcels || uniqueSigefCodes.length === 0) return;
    if (currentIndex >= uniqueSigefCodes.length) {
      // eslint-disable-next-line no-console
      if (VERBOSE) console.log(`[SigefTab] Índice inválido: ${currentIndex}, resetando para 0`);
      setCurrentIndex(0);
      return;
    }
    const currentCode = uniqueSigefCodes[currentIndex];

    // Filtrar parcelas do código atual
    const parcelasDoCodigoAtual = parcels.filter((parcel) => parcel?.codigo_imo === currentCode);
    const nextParcel = parcelasDoCodigoAtual[0] || parcels[0] || null;
    if (nextParcel) setSelectedParcel(nextParcel);

    // Atualizar o código SIGEF selecionado apenas se mudar
    if (!(selectedSigefCodes.length === 1 && selectedSigefCodes[0] === currentCode)) {
      setSelectedSigefCodes([currentCode]);
    }

    // Definir detalhe
    if (Array.isArray(sigefRaw)) {
      const detailFound = sigefRaw.find((sigefItem) => sigefItem?.codigo_imo === currentCode);
      setSelectedSigefDetail(detailFound || nextParcel || null);
    } else {
      setSelectedSigefDetail(nextParcel || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, hasParcels, uniqueSigefCodes, parcels]);

  // A função humanizeKey já está definida no escopo global do arquivo

  // Layout principal
  return (
    <div className="p-4 bg-gray-50 rounded border border-gray-200">
      {/* Cabeçalho com título */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center text-orange-700">
          <Globe2 size={20} className="mr-2" />
          <h3 className="text-lg font-medium">Sistema de Gestão Fundiária</h3>
        </div>
        <RecordNavigator
          currentIndex={currentIndex}
          totalRecords={totalRecords}
          onNavigate={handleNavigate}
        />
      </div>

      {/* Conteúdo principal */}
      <div className="space-y-4">
        {/* Card principal com informações globais */}
        <div className="bg-white rounded-lg border border-orange-200 p-4 shadow-sm">
          <h4 className="text-lg font-medium text-orange-700 mb-4">Informações Globais</h4>

          {/* Resumo geral do SIGEF */}
          <div className="flex flex-wrap gap-4 mb-4">
            {/* Código do imóvel - Exibir código_imo do item SIGEF selecionado */}
            {selectedSigefCodes.length > 0 ? (
              <DataItem
                label="Código SIGEF Selecionado"
                value={selectedSigefCodes[0]}
                icon={<MapPin size={16} className="text-orange-500" />}
                isCopyable
                highlight
              />
            ) : // Se não houver seleção, exibir um código da primeira parcela ou do primeiro item SIGEF
            Array.isArray(sigefData) && sigefData.length > 0 && sigefData[0].codigo_imo ? (
              <DataItem
                label="Código do Imóvel"
                value={sigefData[0].codigo_imo}
                icon={<MapPin size={16} className="text-orange-500" />}
                isCopyable
                highlight
              />
            ) : (
              parcels[0]?.codigo_imo && (
                <DataItem
                  label="Código do Imóvel"
                  value={parcels[0].codigo_imo}
                  icon={<MapPin size={16} className="text-orange-500" />}
                  isCopyable
                  highlight
                />
              )
            )}

            {/* Município */}
            {(sigefData.municipio || (hasParcels && parcels[0].municipio_)) && (
              <DataItem
                label="Município"
                value={sigefData.municipio || parcels[0].municipio_}
                icon={<MapPin size={16} className="text-orange-500" />}
              />
            )}

            {/* UF */}
            {(sigefData.uf || (hasParcels && parcels[0].uf)) && (
              <DataItem
                label="UF"
                value={sigefData.uf || parcels[0].uf}
                icon={<MapPin size={16} className="text-orange-500" />}
              />
            )}

            {/* Número de parcelas */}
            {hasParcels && (
              <div className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-medium flex items-center">
                <FileText size={14} className="mr-1" />
                {parcels.length} {parcels.length === 1 ? 'parcela' : 'parcelas'}
              </div>
            )}

            {/* Status geral */}
            {(sigefData.situacao ||
              sigefData.situacao_do_imovel ||
              (hasParcels && (parcels[0].situacao || parcels[0].situacao_do_imovel))) && (
              <div className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <BadgeCheck size={14} className="inline mr-1" />
                {sigefData.situacao ||
                  sigefData.situacao_do_imovel ||
                  parcels[0].situacao ||
                  parcels[0].situacao_do_imovel ||
                  'Certificada'}
              </div>
            )}
          </div>
        </div>

        {/* Lista de parcelas (quando há várias) */}
        {hasParcels && (
          <div className="mt-4">
            <h4 className="text-lg font-medium text-orange-700 mb-2">
              Parcelas ({filteredParcels.length})
            </h4>
            <div className="grid grid-cols-1 gap-4">
              {filteredParcels.map((parcel, idx) => (
                <SigefParcelCard
                  key={`parcel-${idx}`}
                  parcel={parcel}
                  index={idx}
                  onHighlightGeometry={onHighlightGeometry}
                  isSingleRecord={filteredParcels.length === 1}
                />
              ))}
            </div>
          </div>
        )}

        {/* Caso haja apenas um registro sem parcelas */}
        {!hasParcels && (
          <SigefParcelCard
            parcel={sigefData}
            index={0}
            onHighlightGeometry={onHighlightGeometry}
            isSingleRecord
          />
        )}
      </div>
    </div>
  );
};

// Componente para exibir uma parcela SIGEF
const SigefParcelCard = ({ parcel, index, onHighlightGeometry, isSingleRecord = false }) => {
  // Estado para controle de expansão de detalhes
  const [expanded, setExpanded] = useState(false);

  // Acesso ao contexto de destaque de geometrias
  const { highlightGeometryWithFlash } = useGeometryHighlight();

  // Usando as funções utilitárias importadas do módulo compartilhado

  // Campos principais do SIGEF
  const mainFields = [
    {
      key: 'parcela_co',
      accentKey: 'parcela_co',
      label: 'Código da Parcela',
      icon: <MapPin size={16} className="text-orange-500" />,
    },
    {
      key: 'nome_da_propriedade',
      accentKey: 'nome_da_propriedade',
      label: 'Nome da Propriedade',
      icon: <FileText size={16} className="text-orange-500" />,
    },
    {
      key: 'situacao_do_imovel',
      accentKey: 'situação_do_imóvel',
      label: 'Situação do Imóvel',
      icon: <BadgeCheck size={16} className="text-orange-500" />,
    },
    {
      key: 'responsavel_tecnico',
      accentKey: 'responsável_técnico',
      label: 'Responsável Técnico',
      icon: <UserCheck size={16} className="text-orange-500" />,
    },
    {
      key: 'data_da_aprovacao',
      accentKey: 'data_da_aprovação',
      label: 'Data da Aprovação',
      icon: <Calendar size={16} className="text-orange-500" />,
    },
    {
      key: 'matricula',
      accentKey: 'matrícula',
      label: 'Matrícula',
      icon: <FileText size={16} className="text-orange-500" />,
    },
  ];

  // Função para obter campo com suporte a nomes diferentes
  const getField = (obj, key, accentKey) => {
    if (!obj) return null;
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    if (obj[accentKey] !== undefined && obj[accentKey] !== null) return obj[accentKey];
    return null;
  };

  // Título da parcela
  const parcelTitle =
    getField(parcel, 'nome_da_propriedade', 'nome_da_propriedade') ||
    getField(parcel, 'parcela_co', 'parcela_co') ||
    getField(parcel, 'codigo_imo', 'código_imo') ||
    `Parcela ${index + 1}`;

  // Verificar se tem geometria para destacar
  const hasGeometry = parcel && (parcel.geojson || parcel.geometry || parcel.feature);

  // Função para piscar a geometria 2 vezes quando o botão for clicado
  const handleHighlight = () => {
    if (!parcel) return;
    // ID para identificar a parcela (usar parcela_co, codigo_imo ou índice)
    const featureId = parcel.parcela_co || parcel.codigo_imo || `sigef-parcela-${index}`;

    // Usar o contexto de destaque para fazer a geometria piscar
    highlightGeometryWithFlash('sigef', featureId, parcel);
  };

  return (
    <div
      className={`border rounded-lg ${isSingleRecord ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'} p-3 shadow-sm`}
    >
      {/* Cabeçalho da parcela */}
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-medium text-orange-700">{parcelTitle}</h5>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-gray-400 hover:text-orange-600 focus:outline-none"
          aria-label={expanded ? 'Recolher detalhes' : 'Expandir detalhes'}
        >
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>

      {/* Campos principais sempre visíveis */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
        {mainFields.map(({ key, accentKey, label, icon }) => {
          const value = getField(parcel, key, accentKey);
          if (!value) return null;

          // Tratamento especial para o código da parcela (com botão de destaque)
          if (key === 'parcela_co' && hasGeometry && onHighlightGeometry) {
            return (
              <div key={key} className="flex items-center justify-between">
                <DataItem label={label} value={value} icon={icon} isCopyable />
                <button
                  onClick={handleHighlight}
                  className="ml-2 flex items-center text-xs px-2 py-1 rounded bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200"
                  title="Destacar geometria no mapa (piscar 2x)"
                >
                  <Globe2 size={14} className="mr-1" />
                  Destacar
                </button>
              </div>
            );
          }

          return (
            <DataItem
              key={key}
              label={label}
              value={key.includes('data') ? formatDate(value) : value}
              icon={icon}
            />
          );
        })}
      </div>

      {/* Detalhes expandidos */}
      {expanded && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <h6 className="text-sm font-medium text-gray-500 mb-2">Detalhes adicionais</h6>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(parcel)
              .filter(
                ([key]) =>
                  !mainFields.some((field) => field.key === key || field.accentKey === key) &&
                  key !== 'geojson' &&
                  key !== 'geometry' &&
                  key !== 'feature',
              )
              .map(([key, value]) => {
                if (value === null || value === undefined || value === '') return null;
                return <DataItem key={key} label={humanizeKey(key)} value={formatValue(value)} />;
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SigefTab;
